/**
 * Metric / KPI card.
 *
 * Renders a label, value, optional trend, and optional chevron with a
 * colored top accent stripe. Used on the Dashboard and Reports landing.
 *
 *   <MetricCard
 *     label="Total children"
 *     value={423}
 *     accent="primary"
 *     trend={{ direction: "up", value: "+12 this month" }}
 *     onClick={() => navigate('/profiles')}
 *   />
 */
import React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FiArrowUpRight, FiTrendingUp, FiTrendingDown, FiMinus } from "react-icons/fi";
import AnimatedCounter from "./AnimatedCounter";
import "./MetricCard.css";

const ACCENT = {
    primary: "var(--pt-primary)",
    secondary: "var(--pt-secondary)",
    success: "var(--pt-success)",
    warning: "var(--pt-warning)",
    error: "var(--pt-error)",
    info: "var(--pt-info)",
};

const TREND_ICON = {
    up: FiTrendingUp,
    down: FiTrendingDown,
    flat: FiMinus,
};

export function MetricCard({
    label,
    value,
    trend,
    accent = "primary",
    onClick,
    icon: Icon,
    helperText,
    className = "",
}) {
    const interactive = typeof onClick === "function";
    const accentColor = ACCENT[accent] || ACCENT.primary;
    const TrendIcon = trend ? TREND_ICON[trend.direction] || TREND_ICON.flat : null;

    return (
        <Box
            className={`pt-metric-card ${interactive ? "interactive" : ""} ${className}`}
            onClick={onClick}
            role={interactive ? "button" : undefined}
            tabIndex={interactive ? 0 : undefined}
            onKeyDown={
                interactive
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onClick(e);
                          }
                      }
                    : undefined
            }
        >
            <span
                className="pt-metric-card__accent"
                style={{ background: accentColor }}
                aria-hidden="true"
            />
            <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                        variant="overline"
                        sx={{ color: "var(--pt-text-secondary)", letterSpacing: 0.6 }}
                    >
                        {label}
                    </Typography>
                    {Icon ? (
                        <Box sx={{ color: accentColor, opacity: 0.85 }} aria-hidden="true">
                            <Icon size={18} />
                        </Box>
                    ) : null}
                </Stack>
                <Typography
                    variant="h3"
                    sx={{ fontWeight: 700, lineHeight: 1.15, fontVariantNumeric: "tabular-nums" }}
                >
                    {typeof value === "number" ? <AnimatedCounter value={value} /> : value}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    {trend ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            {TrendIcon && (
                                <Box
                                    sx={{
                                        color:
                                            trend.direction === "up"
                                                ? "var(--pt-success)"
                                                : trend.direction === "down"
                                                ? "var(--pt-error)"
                                                : "var(--pt-text-secondary)",
                                    }}
                                    aria-hidden="true"
                                >
                                    <TrendIcon size={14} />
                                </Box>
                            )}
                            <Typography variant="caption" sx={{ color: "var(--pt-text-secondary)" }}>
                                {trend.value}
                            </Typography>
                        </Stack>
                    ) : helperText ? (
                        <Typography variant="caption" sx={{ color: "var(--pt-text-secondary)" }}>
                            {helperText}
                        </Typography>
                    ) : (
                        <span />
                    )}
                    {interactive && (
                        <Box
                            className="pt-metric-card__chevron"
                            sx={{ color: "var(--pt-text-secondary)" }}
                            aria-hidden="true"
                        >
                            <FiArrowUpRight size={18} />
                        </Box>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
}

export default MetricCard;
